import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { ArrowLeft, Crown, Zap, Check, Lock, Shield, Flame, Sparkles, Gamepad2, TrendingUp, Sword } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const CATEGORY_CONFIG = {
  streak: { label: "Streak", icon: Flame, color: "text-orange-400", bg: "bg-orange-400/10", border: "border-orange-400/30" },
  boost: { label: "Boosts", icon: TrendingUp, color: "text-[#00E5FF]", bg: "bg-[#00E5FF]/10", border: "border-[#00E5FF]/30" },
  quiz: { label: "Quiz Arena", icon: Zap, color: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/30" },
  cosmetic: { label: "Cosmetic", icon: Sparkles, color: "text-purple-400", bg: "bg-purple-400/10", border: "border-purple-400/30" },
};

export default function XPStore() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => { loadStore(); }, []);

  const loadStore = async () => {
    try {
      const res = await api.get("/xp-store/items");
      setStoreData(res.data);
    } catch { toast.error("Failed to load store"); }
    finally { setLoading(false); }
  };

  const purchase = async (itemId) => {
    setPurchasing(itemId);
    try {
      const res = await api.post("/xp-store/purchase", { item_id: itemId });
      toast.success(res.data.message);
      if (res.data.auto_activated) toast("Shield activated!", { icon: "🛡️" });
      updateUser({ total_lifetime_xp: res.data.remaining_xp });
      loadStore();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purchase failed");
    }
    setPurchasing(null);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const categories = ["all", ...new Set((storeData?.items || []).map(i => i.category))];
  const filtered = filter === "all" ? storeData?.items || [] : (storeData?.items || []).filter(i => i.category === filter);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="xp-store-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/rewards")} className="flex items-center gap-2 text-zinc-400 hover:text-white" data-testid="store-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">BTC XP STORE</h1>
          <div className="flex items-center gap-2 bg-[#F7931A]/10 px-3 py-1.5 rounded-lg border border-[#F7931A]/30">
            <Crown className="text-[#F7931A]" size={16} />
            <span className="font-mono text-[#F7931A] text-sm" data-testid="store-xp-balance">{(storeData?.user_xp || 0).toLocaleString()} BTC XP</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Category Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map(cat => {
            const cfg = CATEGORY_CONFIG[cat];
            return (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                  filter === cat
                    ? "bg-[#F7931A] text-black"
                    : "bg-[#161616] text-zinc-400 border border-white/10 hover:border-[#F7931A]/50"
                }`}
                data-testid={`filter-${cat}`}
              >
                {cfg?.icon && <cfg.icon size={14} />}
                {cat === "all" ? "All" : cfg?.label || cat}
              </button>
            );
          })}
        </div>

        {/* Featured: Streak Stabilizers */}
        {filter === "all" && (
          <div className="bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="text-orange-400" size={20} />
              <h2 className="font-heading text-lg font-bold text-white">Streak Stabilizers</h2>
            </div>
            <p className="text-zinc-400 text-sm">Protect your stacking streak. Miss a day? Save it with BTC XP. Going on vacation? Shield your streak for days.</p>
          </div>
        )}

        {/* Items Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item, i) => {
            const cfg = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.boost;
            const Icon = cfg.icon;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className={`bg-[#0A0A0A] border rounded-xl p-5 transition-all hover:border-[#F7931A]/30 ${
                  item.purchased ? "border-green-500/30" : "border-white/10"
                }`}
                data-testid={`store-item-${item.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg ${cfg.bg} flex items-center justify-center`}>
                    <Icon className={cfg.color} size={20} />
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                    {cfg.label}
                  </span>
                </div>
                <h3 className="text-white font-medium mb-1">{item.name}</h3>
                <p className="text-zinc-500 text-xs mb-4 leading-relaxed">{item.description}</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[#F7931A] font-bold">{item.cost.toLocaleString()} BTC XP</span>
                  {item.purchased ? (
                    <span className="flex items-center gap-1 text-green-400 text-xs font-medium">
                      <Check size={14} /> Owned
                    </span>
                  ) : item.can_afford ? (
                    <Button
                      onClick={() => purchase(item.id)}
                      disabled={purchasing === item.id}
                      className="bg-[#F7931A] hover:bg-[#F7931A]/80 text-black text-xs px-4 py-1.5 h-auto font-bold"
                      data-testid={`buy-${item.id}`}
                    >
                      {purchasing === item.id ? "..." : "BUY"}
                    </Button>
                  ) : (
                    <span className="flex items-center gap-1 text-zinc-600 text-xs">
                      <Lock size={14} /> {(item.cost - (storeData?.user_xp || 0)).toLocaleString()} more
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-500">No items in this category yet.</p>
          </div>
        )}
      </main>
    </div>
  );
}
